import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { CartContext } from "../../context/CartContext";
import "./Header.css";

const EcommerceHeader = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hideTopBar, setHideTopBar] = useState(false);

  const lastScrollY = useRef(0);
  const searchBoxRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  const { cartItems, totalItems, totalPrice } = useContext(CartContext);

  // Authentication State
  const token = localStorage.getItem("token");
  const storedUser = JSON.parse(localStorage.getItem("user") || "null");
  const isAuthenticated = !!token && !!storedUser;

  const displayName = storedUser?.name?.trim() || "";
  const initials = displayName
    ? displayName.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase()
    : "G";

  useEffect(() => {
    setShowSearchResults(false);
  }, [location.pathname]);

  // Close search results on outside click or Escape
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        setShowSearchResults(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") setShowSearchResults(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  // Smart Top-bar hide on scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY <= 20) {
        setHideTopBar(false);
        lastScrollY.current = currentScrollY;
        return;
      }
      if (currentScrollY > lastScrollY.current + 5 && currentScrollY > 80) {
        setHideTopBar(true);
      } else if (currentScrollY < lastScrollY.current - 5) {
        setHideTopBar(false);
      }
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Search Debounce
  useEffect(() => {
    const fetchSearchResults = async () => {
      const query = searchTerm.trim();
      if (!query) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      try {
        setLoading(true);
        setShowSearchResults(true);
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL || "http://localhost:5000"}/api/products/search?q=${encodeURIComponent(query)}`
        );
        const data = await res.json();
        setSearchResults(Array.isArray(data) ? data : data.products || []);
      } catch (error) {
        console.error("Search error:", error);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    };

    const delayDebounce = setTimeout(() => {
      fetchSearchResults();
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const query = searchTerm.trim();
    if (!query) return;
    navigate(`/shop?search=${encodeURIComponent(query)}`);
    setShowSearchResults(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  return (
    <div className={`header-shell ${hideTopBar ? "topbar-hidden" : ""}`}>
      <div className="top-bar">
        <div className="container top-bar-content">
          <p>
            <i className="fa-solid fa-truck"></i>
            Free Shipping on Orders Above ₹999
          </p>
          <div className="top-links">
            <Link to="/trackorder">Track Order</Link>
            <Link to="/contact">Support</Link>
          </div>
        </div>
      </div>

      <header className="eco-header">
        <div className="container nav-wrapper">
          <div className="logo">
            <Link to="/">
              <span>Versa</span>core
            </Link>
          </div>

          <form className="search-box" onSubmit={handleSubmit} ref={searchBoxRef}>
            <input
              type="text"
              placeholder="Search products..."
              aria-label="Search products"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => {
                if (searchTerm.trim()) setShowSearchResults(true);
              }}
            />
            <button type="submit" aria-label="Search">
              <i className="fa-solid fa-magnifying-glass"></i>
            </button>

            {showSearchResults && (
              <div className="search-dropdown">
                {loading ? (
                  <div className="search-empty">Searching...</div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((product) => (
                    <Link
                      to={`/product/${product._id}`}
                      key={product._id}
                      className="search-item"
                      onClick={() => {
                        setShowSearchResults(false);
                        setSearchTerm("");
                      }}
                    >
                      <img src={product.image} alt={product.name} />
                      <div className="search-item-info">
                        <h4>{product.name}</h4>
                        <p>₹{product.price}</p>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="search-empty">No products found</div>
                )}
              </div>
            )}
          </form>

          <div className="nav-icons">
            <div className="profile-hover-wrapper">
              <Link to={isAuthenticated ? "/profile" : "/login"} aria-label="Profile">
                <i className="fa-regular fa-user"></i>
              </Link>

              <div className="profile-dropdown">
                {isAuthenticated ? (
                  <>
                    <Link to="/profile" className="profile-dropdown-user">
                      <div className="profile-dropdown-avatar">{initials}</div>
                      <div className="profile-dropdown-user-text">
                        <h4>{displayName}</h4>
                        <span>Welcome to your dashboard</span>
                      </div>
                    </Link>
                    <div className="profile-dropdown-links">
  <Link to="/profile">
    <i className="fa-regular fa-id-card"></i> My Profile
  </Link>
  <Link to="/profile?tab=orders">
    <i className="fa-solid fa-box"></i> Orders
  </Link>
  <Link to="/profile?tab=wishlist">
    <i className="fa-regular fa-heart"></i> Wishlist
  </Link>
  
  {/* 👇 Added these back in! 👇 */}
  <Link to="/profile?tab=address">
    <i className="fa-solid fa-location-dot"></i> Address
  </Link>
  <Link to="/profile?tab=cards">
    <i className="fa-solid fa-credit-card"></i> Saved Cards
  </Link>
  <Link to="/profile?tab=settings">
    <i className="fa-solid fa-gear"></i> Settings
  </Link>
  {/* 👆 ======================= 👆 */}

  <button onClick={handleLogout} className="dropdown-logout-btn">
    <i className="fa-solid fa-right-from-bracket"></i> Logout
  </button>
</div>
                  </>
                ) : (
                  <div className="profile-dropdown-auth">
                    <h4>Welcome to Versacore</h4>
                    <p>Log in to access your orders and wishlist.</p>
                    <Link to="/login" className="auth-btn login-btn">Log In</Link>
                    <Link to="/signup" className="auth-btn register-btn">Sign Up</Link>
                  </div>
                )}
              </div>
            </div>

            <Link to="/wishlist" aria-label="Wishlist">
              <i className="fa-regular fa-heart"></i>
            </Link>

            <div className="cart-hover-wrapper">
              <Link to="/cart" className="cart-icon" aria-label="Cart">
  <i className="fa-solid fa-shopping-bag"></i>
  <span>{totalItems}</span>
</Link>

              {location.pathname !== "/cart" && (
                <div className="mini-cart">
                  <h3>Shopping Cart</h3>
                  {cartItems.length === 0 ? (
                    <p className="empty-mini-cart">Your cart is empty</p>
                  ) : (
                    <>
                      <div className="mini-cart-items">
                        {cartItems.map((item) => (
                          <div className="mini-cart-item" key={item._id}>
                            <img src={item.image} alt={item.name} />
                            <div className="mini-cart-info">
                              <h4>{item.name}</h4>
                              <p>₹{item.price} × {item.quantity}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mini-cart-footer">
                        <h4>Total: <span>₹{totalPrice}</span></h4>
                        <div className="mini-cart-btn-wrapper">
                          <Link to="/cart" className="mini-cart-btn">View Cart & Checkout</Link>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <nav className="menu desktop-menu">
          <div className="container menu-links">
            <NavLink to="/">Home</NavLink>
            <NavLink to="/shop">Shop</NavLink>
            <NavLink to="/new-arrivals">New Arrivals</NavLink>
            <NavLink to="/trending">Trending</NavLink>
            <NavLink to="/categories">Categories</NavLink>
            <NavLink to="/deals">Deals</NavLink>
            <NavLink to="/contact">Contact</NavLink>
          </div>
        </nav>
      </header>
    </div>
  );
};

export default EcommerceHeader;